export const UPLOAD_TIMEOUT_MS = 300_000;
export const UPLOAD_PROGRESS_EVENT = 'ark-upload-progress';

export type UploadField = 'image' | 'attachment' | 'description-image' | 'forum-photo'
  | 'profile-avatar' | 'profile-cover' | 'child-avatar' | 'badge-icon'
  | 'quest-image' | 'activity-image' | 'community-logo' | 'community-cover'
  | 'community-facility' | 'commons-logo' | 'commons-cover'
  | 'ecoregion-avatar' | 'ecoregion-cover';

// Reuse the form labels; upload progress must not introduce parallel translations.
export const UPLOAD_FIELD_LABELS: Record<UploadField, string> = {
  image: 'general.image',
  attachment: 'activity.form.resources',
  'description-image': 'general.image',
  'forum-photo': 'general.image',
  'profile-avatar': 'settings.profile-picture',
  'profile-cover': 'settings.banner',
  'child-avatar': 'settings.profile-picture',
  'badge-icon': 'general.badge-icon',
  'quest-image': 'general.cover-picture',
  'activity-image': 'activity.form.cover-picture',
  'community-logo': 'community.creation-field.logo',
  'community-cover': 'community.creation-field.banner',
  'community-facility': 'community.creation-field.facility-picture',
  'commons-logo': 'community.creation-field.logo',
  'commons-cover': 'community.creation-field.banner',
  'ecoregion-avatar': 'community.creation-field.logo',
  'ecoregion-cover': 'community.creation-field.banner',
};

export type UploadProgressEventDetail = {
  id: string;
  field: UploadField;
  /** The specific failed upload that this attempt is retrying, if any. */
  replaces?: string[];
  state: 'preparing' | 'uploading' | 'finalizing' | 'complete' | 'failed' | 'cancelled';
  progress: number;
};

export type UploadOptions = {
  field?: UploadField;
  retryOf?: string;
  onUploadId?: (id: string) => void;
  signal?: AbortSignal;
  onProgress?: (progress: number) => void;
};

export class ImageUploadError extends Error {
  constructor(message: string, public code: 'UPLOAD_TIMEOUT' | 'UPLOAD_NETWORK_ERROR') {
    super(message);
    this.name = 'ImageUploadError';
  }
}

export function abortReason(signal: AbortSignal): Error {
  return signal.reason ?? new DOMException('The upload was aborted.', 'AbortError');
}

// A failed form submission must not leave sibling uploads running behind it.
export async function uploadBatch<T>(uploads: Array<(signal: AbortSignal) => Promise<T>>): Promise<T[]> {
  const controller = new AbortController();
  try {
    return await Promise.all(uploads.map((upload) => abortableStep(controller.signal, () => upload(controller.signal))));
  } catch (error) {
    controller.abort();
    throw error;
  }
}

// Each preparation/finalization step is bounded, and a late result cannot start
// another request after cancellation, even if its underlying API ignores abort.
export function abortableStep<T>(signal: AbortSignal, operation: () => Promise<T>): Promise<T> {
  if (signal.aborted) return Promise.reject(abortReason(signal));
  return new Promise((resolve, reject) => {
    const abort = () => reject(abortReason(signal));
    signal.addEventListener('abort', abort, { once: true });
    Promise.resolve().then(() => {
      if (signal.aborted) throw abortReason(signal);
      return operation();
    }).then(resolve, reject).finally(() => signal.removeEventListener('abort', abort));
  });
}

export type TrackedUploadOptions = UploadOptions & {
  signal: AbortSignal;
  heartbeat: () => void;
  step: <T>(operation: () => Promise<T>, finalizing?: boolean) => Promise<T>;
};

let uploadIdSequence = 0;

export async function trackUpload<T>(field: UploadField, options: UploadOptions,
  upload: (options: TrackedUploadOptions) => Promise<T>): Promise<T> {
  const id = `${Date.now()}-${++uploadIdSequence}`;
  options.onUploadId?.(id);
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout>;
  let progress = 0;
  let settled = false;
  const publish = (state: UploadProgressEventDetail['state']) => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent<UploadProgressEventDetail>(UPLOAD_PROGRESS_EVENT, {
        detail: {
          id,
          field: options.field ?? field,
          replaces: options.retryOf ? [options.retryOf] : undefined,
          state,
          progress,
        },
      }));
    }
  };
  const heartbeat = () => {
    clearTimeout(timer);
    if (!settled && !controller.signal.aborted) {
      timer = setTimeout(() => controller.abort(new ImageUploadError('Upload made no progress.', 'UPLOAD_TIMEOUT')), UPLOAD_TIMEOUT_MS);
    }
  };
  const cancel = () => controller.abort(options.signal ? abortReason(options.signal) : undefined);
  options.signal?.addEventListener('abort', cancel, { once: true });
  if (options.signal?.aborted) cancel();
  publish('preparing');
  heartbeat();
  try {
    const result = await abortableStep(controller.signal, () => upload({
      ...options,
      signal: controller.signal,
      heartbeat,
      step: async (operation, finalizing = false) => {
        heartbeat();
        if (finalizing) publish('finalizing');
        return abortableStep(controller.signal, operation);
      },
      onProgress: (value) => {
        if (settled || controller.signal.aborted) return;
        progress = value;
        options.onProgress?.(value);
        publish(value >= 100 ? 'finalizing' : 'uploading');
      },
    }));
    settled = true;
    progress = 100;
    publish('complete');
    return result;
  } catch (error) {
    settled = true;
    publish(options.signal?.aborted ? 'cancelled' : 'failed');
    throw error;
  } finally {
    clearTimeout(timer);
    options.signal?.removeEventListener('abort', cancel);
  }
}
